import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { trpc } from "@/trpc/trpc";
import { useAuth } from "@/lib/auth";
import { PRIVACY_POLICY_URL } from "@/lib/legal";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const emailSchema = z.object({
  email: z.string().email("Podaj poprawny adres email"),
});

const otpSchema = z.object({
  code: z.string().length(6, "Kod musi mieć 6 cyfr"),
});

function LoginPage() {
  const navigate = useNavigate();
  const { setTokens } = useAuth();
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");

  const requestOtp = trpc.auth.requestOtp.useMutation({
    onSuccess: () => {
      setStep("otp");
      toast.success("Kod został wysłany na email");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const verifyOtp = trpc.auth.verifyOtp.useMutation({
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
      navigate({ to: "/" });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const otpForm = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: "" },
  });

  const handleEmailSubmit = emailForm.handleSubmit((data) => {
    setEmail(data.email);
    requestOtp.mutate({ email: data.email });
  });

  const handleOtpSubmit = otpForm.handleSubmit((data) => {
    verifyOtp.mutate({ email, code: data.code });
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold">Panel salonu</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {step === "email"
              ? "Zaloguj się przez email"
              : "Wpisz kod z emaila"}
          </p>
        </div>

        {step === "email" ? (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="twoj@email.pl"
                {...emailForm.register("email")}
              />
              {emailForm.formState.errors.email && (
                <p className="text-sm text-destructive">
                  {emailForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={requestOtp.isPending}
            >
              {requestOtp.isPending ? "Wysyłanie..." : "Wyślij kod"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Kod weryfikacyjny</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                className="text-center text-2xl tracking-widest"
                {...otpForm.register("code")}
              />
              {otpForm.formState.errors.code && (
                <p className="text-sm text-destructive">
                  {otpForm.formState.errors.code.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={verifyOtp.isPending}
            >
              {verifyOtp.isPending ? "Weryfikacja..." : "Zaloguj się"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setStep("email");
                otpForm.reset();
              }}
            >
              Zmień email
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Logując się, akceptujesz{" "}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            Politykę prywatności
          </a>
          .
        </p>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <Link to="/licenses" className="underline underline-offset-2">
            Licencje open source
          </Link>
        </p>
      </Card>
    </div>
  );
}
